import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useRecipes } from '@/contexts/RecipesContext';
import { Button } from '@/components/ui/button';
import Layout from '@/components/Layout';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Bookmark, Clock, Utensils, Users, ArrowLeft, CalendarPlus, ArrowUp, ArrowDown, ListCheck } from 'lucide-react';
import { Spinner } from '@/components/Spinner';
import NutriscoreBar from '@/components/NutriscoreBar';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useMenu } from '@/contexts/MenuContext';
import RecipeLikeButton from '@/components/RecipeLikeButton';
import RecipeComments from '@/components/RecipeComments';
import RecipeRatings from '@/components/RecipeRatings';
import RecipeErrorReportForm from '@/components/RecipeErrorReportForm';
import ProtectedRoute from '@/components/ProtectedRoute';
import RecipeShareDialog from '@/components/RecipeShareDialog';
import { triggerFavoriteAdd, triggerMenuAdd } from '@/utils/onboardingEvents';
import { format, addDays, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useIsMobile } from '@/hooks/use-mobile';

const RecipeDetail = () => {
  const { id: recipeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { recipes, loading, toggleFavorite, isFavorite, getRecipe } = useRecipes();
  const { user } = useAuth();
  const { addRecipeToMenu, availableMealTypes } = useMenu();
  
  const [isAddingToMenu, setIsAddingToMenu] = useState(false);
  const [selectedDay, setSelectedDay] = useState('monday');
  const [selectedMealType, setSelectedMealType] = useState('lunch');
  const [selectedPortionSize, setSelectedPortionSize] = useState('1');
  const [selectedServings, setSelectedServings] = useState(1.0);
  const [addToMenuDialogOpen, setAddToMenuDialogOpen] = useState(false);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  // Add state to track completed instruction steps
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  // Add state for recipe added success message
  const [recipeAddedSuccess, setRecipeAddedSuccess] = useState(false);
  
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const daysWithDates = [
    { key: 'monday', name: 'Lunes', date: addDays(currentWeekStart, 0) },
    { key: 'tuesday', name: 'Martes', date: addDays(currentWeekStart, 1) },
    { key: 'wednesday', name: 'Miércoles', date: addDays(currentWeekStart, 2) },
    { key: 'thursday', name: 'Jueves', date: addDays(currentWeekStart, 3) },
    { key: 'friday', name: 'Viernes', date: addDays(currentWeekStart, 4) },
    { key: 'saturday', name: 'Sábado', date: addDays(currentWeekStart, 5) },
    { key: 'sunday', name: 'Domingo', date: addDays(currentWeekStart, 6) },
    { key: 'monday2', name: 'Lunes (próx.)', date: addDays(currentWeekStart, 7) },
    { key: 'tuesday2', name: 'Martes (próx.)', date: addDays(currentWeekStart, 8) },
    { key: 'wednesday2', name: 'Miércoles (próx.)', date: addDays(currentWeekStart, 9) },
    { key: 'thursday2', name: 'Jueves (próx.)', date: addDays(currentWeekStart, 10) },
    { key: 'friday2', name: 'Viernes (próx.)', date: addDays(currentWeekStart, 11) },
    { key: 'saturday2', name: 'Sábado (próx.)', date: addDays(currentWeekStart, 12) },
    { key: 'sunday2', name: 'Domingo (próx.)', date: addDays(currentWeekStart, 13) }
  ];
  
  // Optimize useEffect with proper dependencies
  useEffect(() => {
    if (recipeId && !loading) {
      const recipe = getRecipe(recipeId);
      setCurrentRecipe(recipe);
      
      // Reset completed steps when loading a new recipe
      setCompletedSteps([]);
    }
  }, [recipeId, loading, getRecipe]);

  // Check if a step is completed
  const isStepCompleted = (index: number) => {
    return completedSteps.includes(`step-${index}`);
  };

  // Toggle step completion
  const toggleStepCompletion = (index: number) => {
    const stepId = `step-${index}`;
    setCompletedSteps((prev) => {
      if (prev.includes(stepId)) {
        return prev.filter(id => id !== stepId);
      } else {
        return [...prev, stepId];
      }
    });
  };

  useEffect(() => {
    const handleLikeUpdate = (event: CustomEvent) => {
      if (event.detail.recipeId === recipeId) {
        console.log("Like update detected for current recipe");
      }
    };

    window.addEventListener('recipe-like-updated', handleLikeUpdate as EventListener);
    
    return () => {
      window.removeEventListener('recipe-like-updated', handleLikeUpdate as EventListener);
    };
  }, [recipeId]);

  useEffect(() => {
    if (availableMealTypes.length > 0 && !availableMealTypes.includes(selectedMealType)) {
      setSelectedMealType(availableMealTypes[0]);
    }
  }, [availableMealTypes, selectedMealType]);

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-96">
          <Spinner size="lg" />
        </div>
      </Layout>
    );
  }

  const recipe = currentRecipe || (recipeId ? getRecipe(recipeId) : null);

  if (!recipe) {
    return (
      <Layout>
        <div className="container mx-auto py-12 px-4">
          <h1 className="text-2xl font-bold mb-4">Receta no encontrada</h1>
          <p>Lo sentimos, la receta que buscas no existe.</p>
          <p className="text-sm text-gray-500 mt-2">ID buscado: {recipeId || "No ID"}</p>
          <Button 
            onClick={() => navigate('/recetas')}
            className="mt-4"
          >
            Volver a recetas
          </Button>
        </div>
      </Layout>
    );
  }

  const adjustIngredientAmount = (amount: number, multiplier: number) => {
    return Math.round((amount * multiplier) * 10) / 10;
  };

  const sanitizedRecipe = {
    ...recipe,
    title: recipe.title || 'Sin título',
    description: recipe.description || 'Sin descripción',
    imageUrl: recipe.imageUrl || 'https://images.unsplash.com/photo-1618160702438-9b02ab6515c9',
    prepTime: typeof recipe.prepTime === 'number' ? recipe.prepTime : 0,
    cookTime: typeof recipe.cookTime === 'number' ? recipe.cookTime : 0,
    servings: typeof recipe.servings === 'number' ? recipe.servings : 1,
    calories: typeof recipe.calories === 'number' ? recipe.calories : 0,
    protein: typeof recipe.protein === 'number' ? recipe.protein : 0,
    carbs: typeof recipe.carbs === 'number' ? recipe.carbs : 0,
    fat: typeof recipe.fat === 'number' ? recipe.fat : 0,
    iron: typeof recipe.iron === 'number' ? recipe.iron : 0,
    calcium: typeof recipe.calcium === 'number' ? recipe.calcium : 0,
    zinc: typeof recipe.zinc === 'number' ? recipe.zinc : 0,
    magnesium: typeof recipe.magnesium === 'number' ? recipe.magnesium : 0,
    omega3: typeof recipe.omega3 === 'number' ? recipe.omega3 : 0,
    nutriscore: typeof recipe.nutriscore === 'number' ? recipe.nutriscore : 50,
  };

  const recipeIngredients = Array.isArray(recipe.ingredients) 
    ? recipe.ingredients.filter(ing => 
        ing && typeof ing === 'object' && ing.name && ing.name.trim() !== 'Sin nombre'
      ).map(ing => ({
        ...ing,
        name: ing.name?.trim() || 'Ingrediente',
        amount: typeof ing.amount === 'number' ? ing.amount : 0,
        unit: ing.unit?.trim() || 'unidad'
      }))
    : [];

  const recipeInstructions = Array.isArray(recipe.instructions) 
    ? recipe.instructions
        .filter(instr => instr && typeof instr === 'string' && instr.trim() !== '')
        .map(instr => {
          let cleaned = String(instr);
          cleaned = cleaned.replace(/["\\{}\[\]]+/g, '');
          cleaned = cleaned.replace(/(amount|unit|name|id)[\s:]*[,]?/g, '');
          return cleaned.trim();
        })
    : [];

  const recipeTags = Array.isArray(recipe.tags) 
    ? recipe.tags.filter(tag => tag && typeof tag === 'string' && tag.trim() !== '')
    : [];

  const handleToggleFavorite = () => {
    if (user) {
      const wasFavorite = isFavorite(recipe.id);
      toggleFavorite(recipe.id);
      
      if (!wasFavorite) {
        triggerFavoriteAdd();
        console.log("Triggered favorite-added event from RecipeDetail");
      }
    } else {
      toast.error("Debes iniciar sesión para guardar favoritos");
    }
  };

  const handleAddToMenu = async () => {
    setIsAddingToMenu(true);
    try {
      await addRecipeToMenu(
        recipe.id, 
        selectedDay, 
        selectedMealType, 
        parseFloat(selectedPortionSize)
      );
      
      triggerMenuAdd();
      console.log("Triggered menu-added event from RecipeDetail");
      
      toast.success(`Receta añadida al ${getMealTypeName(selectedMealType)} del ${getDayName(selectedDay)}`);
      setAddToMenuDialogOpen(false);
      
      // Set recipe added success state and auto-hide after 5 seconds
      setRecipeAddedSuccess(true);
      setTimeout(() => {
        setRecipeAddedSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error("Error adding recipe to menu:", error);
      toast.error("Error al añadir la receta al menú");
    } finally {
      setIsAddingToMenu(false);
    }
  };

  const handleServingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(event.target.value);
    if (value >= 0.1 && value <= 8.0) {
      setSelectedServings(value);
    }
  };

  const handleIncrementServings = () => {
    setSelectedServings(prev => Math.min(prev + 0.1, 8.0));
  };

  const handleDecrementServings = () => {
    setSelectedServings(prev => Math.max(prev - 0.1, 0.1));
  };

  function getDayName(day: string): string {
    const days: Record<string, string> = {
      monday: 'Lunes',
      tuesday: 'Martes',
      wednesday: 'Miércoles',
      thursday: 'Jueves',
      friday: 'Viernes',
      saturday: 'Sábado',
      sunday: 'Domingo',
      monday2: 'Lunes (próx.)',
      tuesday2: 'Martes (próx.)',
      wednesday2: 'Miércoles (próx.)',
      thursday2: 'Jueves (próx.)',
      friday2: 'Viernes (próx.)',
      saturday2: 'Sábado (próx.)',
      sunday2: 'Domingo (próx.)',
    };
    return days[day] || day;
  }

  function getMealTypeName(mealType: string): string {
    const meals: Record<string, string> = {
      breakfast: 'Desayuno',
      morning_snack: 'Media mañana',
      lunch: 'Almuerzo',
      afternoon_snack: 'Merienda',
      dinner: 'Cena',
      evening_snack: 'Tentempié nocturno',
    };
    return meals[mealType] || mealType;
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="container mx-auto py-12 px-4">
          <div className="mb-6">
            <Link to="/recetas" className="flex items-center text-vegana-primary hover:text-vegana-secondary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a recetas
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-2">
              <div className="rounded-lg overflow-hidden shadow-lg h-80 md:h-96 relative">
                <img
                  src={sanitizedRecipe.imageUrl}
                  alt={sanitizedRecipe.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 bg-black/60 text-white px-2 py-1 text-xs text-left">
                  Sugerencia de presentación
                </div>
                <RecipeErrorReportForm
                  recipeId={recipe.id}
                  recipeName={sanitizedRecipe.title}
                />
              </div>
              
              {/* Share banner right below the image */}
              <RecipeShareDialog recipe={sanitizedRecipe} />
            </div>

            <div>
              <div className="flex justify-between items-start">
                <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">{sanitizedRecipe.title}</h1>
                <div className="flex items-center gap-3">
                  <div className="z-10">
                    <RecipeLikeButton 
                      recipeId={recipe.id} 
                      key={`like-button-${recipe.id}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`${
                      user && isFavorite(recipe.id)
                        ? 'text-blue-600 hover:text-blue-700'
                        : 'text-gray-400 hover:text-blue-600'
                    }`}
                    onClick={handleToggleFavorite}
                    title="Guardar en favoritos"
                  >
                    <Bookmark 
                      className={`h-6 w-6 ${user && isFavorite(recipe.id) ? 'fill-current' : ''}`} 
                    />
                  </Button>
                </div>
              </div>

              <p className="text-gray-600 mb-6">{sanitizedRecipe.description}</p>

              <div className="flex flex-wrap gap-2 mb-6">
                {recipeTags.map((tag, index) => (
                  <Badge key={index} variant="outline" className="bg-vegana-primary/10 text-vegana-primary border-vegana-primary/30">
                    {tag}
                  </Badge>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="flex flex-col items-center p-3 bg-vegana-primary/5 rounded-lg">
                  <Clock className="h-5 w-5 text-vegana-primary mb-1" />
                  <span className="text-xs text-gray-500">Preparación</span>
                  <span className="font-medium">{sanitizedRecipe.prepTime} min</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-vegana-primary/5 rounded-lg">
                  <Utensils className="h-5 w-5 text-vegana-primary mb-1" />
                  <span className="text-xs text-gray-500">Cocción</span>
                  <span className="font-medium">{sanitizedRecipe.cookTime} min</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-vegana-primary/5 rounded-lg">
                  <Users className="h-5 w-5 text-vegana-primary mb-1" />
                  <span className="text-xs text-gray-500">Porciones</span>
                  <span className="font-medium">{sanitizedRecipe.servings}</span>
                </div>
                <div className="flex flex-col items-center p-3 bg-vegana-primary/5 rounded-lg">
                  <span className="text-vegana-primary font-bold text-lg mb-1">N</span>
                  <span className="text-xs text-gray-500">Nutriscore</span>
                  <NutriscoreBar value={sanitizedRecipe.nutriscore} readonly />
                </div>
              </div>

              <div className="mb-8">
                <h3 className="font-semibold text-lg mb-2">Información nutricional (por porción)</h3>
                
                <div>
                  <h4 className="text-sm font-semibold mb-1 text-gray-600">Macronutrientes</h4>
                  <div className="grid grid-cols-4 gap-3 text-center mb-4">
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="text-xl font-bold text-gray-800">{sanitizedRecipe.calories}</div>
                      <div className="text-xs text-gray-500">calorías</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="text-xl font-bold text-blue-600">{sanitizedRecipe.protein}g</div>
                      <div className="text-xs text-gray-500">proteínas</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="text-xl font-bold text-amber-600">{sanitizedRecipe.carbs}g</div>
                      <div className="text-xs text-gray-500">carbohidratos</div>
                    </div>
                    <div className="bg-gray-50 p-2 rounded-lg">
                      <div className="text-xl font-bold text-green-600">{sanitizedRecipe.fat}g</div>
                      <div className="text-xs text-gray-500">grasas</div>
                    </div>
                  </div>
                  
                  <h4 className="text-sm font-semibold mb-1 text-gray-600">Micronutrientes</h4>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-center">
                    {sanitizedRecipe.iron !== undefined && sanitizedRecipe.iron > 0 && (
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <div className="text-lg font-bold text-red-600">{sanitizedRecipe.iron}mg</div>
                        <div className="text-xs text-gray-500">hierro</div>
                      </div>
                    )}
                    {sanitizedRecipe.calcium !== undefined && sanitizedRecipe.calcium > 0 && (
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <div className="text-lg font-bold text-blue-500">{sanitizedRecipe.calcium}mg</div>
                        <div className="text-xs text-gray-500">calcio</div>
                      </div>
                    )}
                    {sanitizedRecipe.zinc !== undefined && sanitizedRecipe.zinc > 0 && (
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <div className="text-lg font-bold text-purple-600">{sanitizedRecipe.zinc}mg</div>
                        <div className="text-xs text-gray-500">zinc</div>
                      </div>
                    )}
                    {sanitizedRecipe.magnesium !== undefined && sanitizedRecipe.magnesium > 0 && (
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <div className="text-lg font-bold text-teal-600">{sanitizedRecipe.magnesium}mg</div>
                        <div className="text-xs text-gray-500">magnesio</div>
                      </div>
                    )}
                    {sanitizedRecipe.omega3 !== undefined && sanitizedRecipe.omega3 > 0 && (
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <div className="text-lg font-bold text-blue-700">{sanitizedRecipe.omega3}g</div>
                        <div className="text-xs text-gray-500">omega 3</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Dialog open={addToMenuDialogOpen} onOpenChange={setAddToMenuDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full mb-4 bg-vegana-primary hover:bg-vegana-secondary">
                      <CalendarPlus className="h-4 w-4 mr-2" />
                      Añadir al Menú Semanal
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Añadir a Menú Semanal</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <div className="grid gap-2">
                        <label className="text-sm font-medium">Seleccionar día</label>
                        <Select value={selectedDay} onValueChange={setSelectedDay}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecciona un día" />
                          </SelectTrigger>
                          <SelectContent>
                            {daysWithDates.map((day) => (
                              <SelectItem key={day.key} value={day.key}>
                                {day.name} ({format(day.date, "d MMM", { locale: es })})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <label className="text-sm font-medium">Seleccionar comida</label>
                        <Select value={selectedMealType} onValueChange={setSelectedMealType}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecciona una comida" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableMealTypes.map((mealType) => (
                              <SelectItem key={mealType} value={mealType}>
                                {getMealTypeName(mealType)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-2">
                        <label className="text-sm font-medium">Tamaño de porción</label>
                        <Select value={selectedPortionSize} onValueChange={setSelectedPortionSize}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Selecciona el tamaño de porción" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0.5">Media porción (x0.5)</SelectItem>
                            <SelectItem value="1">Porción estándar (x1)</SelectItem>
                            <SelectItem value="2">Porción doble (x2)</SelectItem>
                            <SelectItem value="3">Porción triple (x3)</SelectItem>
                            <SelectItem value="4">Porción cuádruple (x4)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setAddToMenuDialogOpen(false)}>Cancelar</Button>
                      <Button 
                        onClick={handleAddToMenu} 
                        className="bg-vegana-primary hover:bg-vegana-secondary"
                        disabled={isAddingToMenu}
                      >
                        {isAddingToMenu ? 'Añadiendo...' : 'Añadir al Menú'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                {/* Add success message with link to menu */}
                {recipeAddedSuccess && (
                  <div className="text-green-600 text-sm bg-green-50 p-3 rounded-md border border-green-200 animate-fade-in">
                    Receta añadida. <Link to="/menu-semanal" className="font-medium underline hover:text-vegana-primary">Ir al menú semanal</Link>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-12">
            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold pb-2 border-b border-gray-200">Ingredientes</h2>
                <div className="flex items-center gap-2">
                  <label htmlFor="servings" className="text-sm font-medium">
                    Porciones:
                  </label>
                  <div className="flex items-center bg-gray-100 rounded-md border border-gray-300">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 text-vegana-primary hover:bg-vegana-primary/20 flex items-center justify-center"
                      onClick={handleDecrementServings}
                    >
                      <ArrowDown className="h-5 w-5" />
                      <span className="sr-only">Reducir</span>
                    </Button>
                    
                    <div className="w-10 h-8 flex items-center justify-center font-medium">
                      {selectedServings.toFixed(1)}
                    </div>
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="h-8 w-8 text-vegana-primary hover:bg-vegana-primary/20 flex items-center justify-center"
                      onClick={handleIncrementServings}
                    >
                      <ArrowUp className="h-5 w-5" />
                      <span className="sr-only">Aumentar</span>
                    </Button>
                  </div>
                </div>
              </div>
              
              {recipeIngredients.length > 0 ? (
                <ul className="space-y-3">
                  {recipeIngredients.map((ingredient, index) => (
                    <li key={index} className="flex items-start">
                      <span className="inline-block w-2 h-2 bg-vegana-primary rounded-full mt-2 mr-3"></span>
                      <span>
                        <strong>
                          {adjustIngredientAmount(ingredient.amount, selectedServings)} {ingredient.unit}
                        </strong>{' '}
                        {ingredient.name}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 italic">No hay ingredientes disponibles</p>
              )}
            </div>

            <div>
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold pb-2 border-b border-gray-200">Instrucciones</h2>
                <div className="flex items-center text-sm text-gray-600">
                  <ListCheck className="h-4 w-4 mr-1" /> 
                  <span>Marca los pasos completados</span>
                </div>
              </div>
              
              {recipeInstructions.length > 0 ? (
                <ol className="space-y-6 text-left">
                  {recipeInstructions.map((step, index) => (
                    <li key={index} className="flex items-start group">
                      <div className="flex-shrink-0 mr-4">
                        <Checkbox 
                          id={`step-${index}`}
                          checked={isStepCompleted(index)}
                          onCheckedChange={() => toggleStepCompletion(index)}
                          className="mt-1 border-2 border-vegana-primary data-[state=checked]:bg-vegana-primary data-[state=checked]:text-white"
                        />
                      </div>
                      <div className="flex-grow">
                        <label 
                          htmlFor={`step-${index}`} 
                          className={`flex items-center cursor-pointer ${isStepCompleted(index) ? 'line-through text-gray-500' : ''}`}
                        >
                          <span className="flex-shrink-0 w-8 h-8 rounded-full bg-vegana-primary flex items-center justify-center text-white font-bold mr-3">
                            {index + 1}
                          </span>
                          <p className="mt-1">{step}</p>
                        </label>
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-gray-500 italic">No hay instrucciones disponibles</p>
              )}
            </div>
          </div>

          <RecipeRatings recipeId={recipe.id} />
          
          <RecipeComments 
            recipeId={recipe.id} 
            key={`comments-${recipe.id}`}
          />
        </div>
      </Layout>
    </ProtectedRoute>
  );
};

export default RecipeDetail;
